import { useMutation } from '@apollo/client/react';
import { ADD_AND_SEND_EMAIL_VERIFICATION_CODE_MUTATION } from '../../integrations/allkort/graphql/gql/EmailMutations';
import { ResendEmailVerificationCodeResponse } from '../../integrations/allkort/graphql/interface/Emails';

/**
 * useResendEmailVerificationCode
 *
 * Resends a verification code to a previously added (but unverified) email address.
 *
 * @example
 * ```tsx
 * const { resendCode, loading } = useResendEmailVerificationCode();
 *
 * const handleResend = async () => {
 *   const result = await resendCode({ emailId: 382355 });
 *   if (result) console.log('Code resent to:', result.address);
 * };
 * ```
 */
export const useResendEmailVerificationCode = () => {
	const [resendMutation, { loading, error, data }] =
		useMutation<ResendEmailVerificationCodeResponse>(
			ADD_AND_SEND_EMAIL_VERIFICATION_CODE_MUTATION
		);

	/**
	 * Resend the email verification code
	 * @param emailId - The ID of the unverified email (from addEmails response)
	 * @returns The email address and id on success, or throws on failure
	 */
	const resendCode = async ({ emailId }: { emailId: number }) => {
		try {
			const result = await resendMutation({
				variables: { emailId },
			});

			if (result.error) {
				const apolloError = result.error as any;
				const message = apolloError.graphQLErrors?.[0]?.message ?? apolloError.message;
				console.error('[useResendEmailVerificationCode] GraphQL error:', message);
				throw new Error(message);
			}

			const email = result.data?.addAndSendEmailVerificationCode?.email ?? null;
			console.log('[useResendEmailVerificationCode] Code resent to:', email?.address);
			return email;
		} catch (err) {
			throw err;
		}
	};

	return { resendCode, loading, error, data };
};
